import { getUser } from "../utils/authService";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, Calendar, User, Download, CheckCircle, XCircle, Clock, Send, Lock, Edit, Trash2, Eye, Layers, ChevronDown, ChevronUp, Building2, HardHat } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import BackNavigation from "../components/BackNavigation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DPRList = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [dprEntries, setDprEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedDpr, setSelectedDpr] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // New states for View and Consolidated DPR
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewDprData, setViewDprData] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" or "consolidated"
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [expandedDates, setExpandedDates] = useState({});
  const [dateFilter, setDateFilter] = useState({ start: "", end: "" });
  const [pendingMaterialDprs, setPendingMaterialDprs] = useState(0); // Count of DPRs without material deduction

  const currentUser = getUser() || {};
  const canApprove = ["Master Admin", "Management", "Project Director", "Project Manager"].includes(currentUser.role);
  
  // Check if user can edit DPR (Master Admin, or has DPR edit permission)
  const canEditDPR = () => {
    if (["Master Admin", "Management", "Project Director", "Project Manager"].includes(currentUser.role)) {
      return true;
    }
    const modules = currentUser.modules || [];
    return modules.some(m => m.module?.toLowerCase() === "dpr" && m.can_edit);
  };

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, dprRes] = await Promise.all([
        apiClient.get(`/projects/${projectId}`),
        apiClient.get(`/dpr/${projectId}`)
      ]);
      setProject(projectRes.data);
      setDprEntries(dprRes.data);
      
      // Count DPRs that don't have material deduction done yet
      const pendingCount = dprRes.data.filter(dpr => 
        !dpr.materials_recalculated && 
        !dpr.auto_deducted_materials?.length &&
        dpr.boq_item_id // Only count those linked to BOQ (which have recipes)
      ).length;
      setPendingMaterialDprs(pendingCount);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const exportDPR = async (dprId, activity, date) => {
    try {
      const response = await apiClient.get(`/export/dpr/${dprId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DPR_${date}_${activity.substring(0, 20)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("DPR exported successfully");
    } catch (error) {
      toast.error("Failed to export DPR");
    }
  };

  const submitDPR = async (dprId) => {
    try {
      await apiClient.put(`/dpr/${dprId}/submit`);
      toast.success("DPR submitted for approval");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit DPR");
    }
  };

  const approveDPR = async (dprId) => {
    try {
      await apiClient.put(`/dpr/${dprId}/approve`);
      toast.success("DPR approved successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve DPR");
    }
  };

  const openRejectDialog = (dpr) => {
    setSelectedDpr(dpr);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const rejectDPR = async () => {
    if (!selectedDpr) return;
    try {
      await apiClient.put(`/dpr/${selectedDpr.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success("DPR rejected");
      setRejectDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject DPR");
    }
  };

  const openEditDialog = (dpr) => {
    setSelectedDpr(dpr);
    setEditForm({
      activity: dpr.activity || "",
      quantity: dpr.quantity || 0,
      unit: dpr.unit || "",
      workers_count: dpr.workers_count || 0,
      remarks: dpr.remarks || ""
    });
    setEditDialogOpen(true);
  };

  const editDPR = async () => {
    if (!selectedDpr) return;
    try {
      await apiClient.put(`/dpr/${selectedDpr.id}/edit`, editForm);
      toast.success("DPR updated successfully");
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update DPR");
    }
  };

  const deleteDPR = async (dprId) => {
    if (!window.confirm("Are you sure you want to delete this DPR entry?")) return;
    try {
      await apiClient.delete(`/dpr/${dprId}`);
      toast.success("DPR deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete DPR");
    }
  };

  // Recalculate materials for a single DPR based on BOQ recipe
  const recalculateDPRMaterials = async (dprId, activity) => {
    if (!window.confirm(`Recalculate and deduct materials for DPR: "${activity}"?\n\nThis will deduct materials from stock based on the BOQ recipe.`)) return;
    try {
      const response = await apiClient.post(`/dpr/${dprId}/recalculate-materials`);
      const { materials_deducted } = response.data;
      if (materials_deducted && materials_deducted.length > 0) {
        const matList = materials_deducted.map(m => `${m.material_name}: ${m.quantity.toFixed(2)}`).join(', ');
        toast.success(`Materials deducted: ${matList}`);
      } else {
        toast.success("Materials recalculated successfully");
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to recalculate materials");
    }
  };

  // Bulk recalculate materials for all DPRs in project
  const bulkRecalculateMaterials = async () => {
    if (!window.confirm(`Bulk recalculate materials for ALL DPRs in this project?\n\nThis will process all DPRs that haven't been calculated yet and deduct materials based on BOQ recipes.`)) return;
    try {
      const response = await apiClient.post(`/dpr/bulk-recalculate-materials/${projectId}`);
      const { dprs_processed, dprs_skipped, total_materials_deducted } = response.data;
      toast.success(`Processed ${dprs_processed} DPRs, skipped ${dprs_skipped}, deducted ${total_materials_deducted} material entries`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk recalculate materials");
    }
  };

  // View single DPR with full details
  const viewDPR = async (dprId) => {
    try {
      const response = await apiClient.get(`/dpr/detail/${dprId}`);
      setViewDprData(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load DPR details");
    }
  };

  // Fetch consolidated DPR data
  const fetchConsolidatedDPR = async () => {
    try {
      let url = `/dpr/consolidated/${projectId}`;
      const params = [];
      if (dateFilter.start) params.push(`start_date=${dateFilter.start}`);
      if (dateFilter.end) params.push(`end_date=${dateFilter.end}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      
      const response = await apiClient.get(url);
      setConsolidatedData(response.data);
    } catch (error) {
      toast.error("Failed to load consolidated DPR");
    }
  };

  // Toggle date expansion in consolidated view
  const toggleDateExpansion = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Export consolidated DPR as PDF
  const exportConsolidatedPDF = async () => {
    try {
      let url = `/export/dpr-consolidated/${projectId}`;
      const params = [];
      if (dateFilter.start) params.push(`start_date=${dateFilter.start}`);
      if (dateFilter.end) params.push(`end_date=${dateFilter.end}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      
      const response = await apiClient.get(url, { responseType: 'blob' });
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Consolidated_DPR_${project?.name || 'Report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Consolidated DPR exported successfully");
    } catch (error) {
      toast.error("Failed to export consolidated DPR");
    }
  };

  // Effect to fetch consolidated data when switching to consolidated view
  useEffect(() => {
    if (viewMode === "consolidated" && !consolidatedData) {
      fetchConsolidatedDPR();
    }
  }, [viewMode]);

  const getStatusBadge = (status, isLocked) => {
    const statusConfig = {
      "Draft": { color: "bg-slate-200 text-slate-700", icon: FileText },
      "Submitted": { color: "bg-warning/20 text-warning", icon: Clock },
      "Approved": { color: "bg-success/20 text-success", icon: CheckCircle },
      "Rejected": { color: "bg-error/20 text-error", icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig["Draft"];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-sm text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        <span>{status || "Draft"}</span>
        {isLocked && <Lock className="w-3 h-3 ml-1" />}
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div>
      <BackNavigation title="DPR Entries" projectName={project?.name} />
      
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">DPR ENTRIES</h1>
          <p className="text-slate-600 mt-2 font-body">{project?.name}</p>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex items-center space-x-2">
          {currentUser.role === "Master Admin" && pendingMaterialDprs > 0 && (
            <button
              onClick={bulkRecalculateMaterials}
              data-testid="bulk-recalculate-materials-btn"
              className="flex items-center space-x-2 px-4 py-2 rounded-sm text-sm font-medium bg-warning/20 text-warning hover:bg-warning/30 transition-colors"
              title={`Recalculate materials for ${pendingMaterialDprs} DPRs based on BOQ recipes`}
            >
              <Layers className="w-4 h-4" />
              <span>Recalculate Materials ({pendingMaterialDprs})</span>
            </button>
          )}
          <button
            onClick={() => setViewMode("list")}
            data-testid="dpr-list-view-btn"
            className={`flex items-center space-x-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              viewMode === "list" 
                ? "bg-primary text-white" 
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>List View</span>
          </button>
          <button
            onClick={() => setViewMode("consolidated")}
            data-testid="dpr-consolidated-view-btn"
            className={`flex items-center space-x-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              viewMode === "consolidated" 
                ? "bg-primary text-white" 
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            <Layers className="w-4 h-4" />
            <span>Consolidated</span>
          </button>
        </div>
      </div>

      {/* Consolidated View */}
      {viewMode === "consolidated" && (
        <div className="space-y-6">
          {/* Date Filter & Export */}
          <div className="stat-card">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-widest">Start Date</label>
                <input
                  type="date"
                  value={dateFilter.start}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-widest">End Date</label>
                <input
                  type="date"
                  value={dateFilter.end}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button
                onClick={fetchConsolidatedDPR}
                className="btn-primary text-sm"
              >
                Apply Filter
              </button>
              <button
                onClick={exportConsolidatedPDF}
                data-testid="export-consolidated-dpr-btn"
                className="btn-outline flex items-center space-x-2 text-sm"
              >
                <Download className="w-4 h-4" />
                <span>Export PDF</span>
              </button>
            </div>
          </div>

          {/* Grand Summary */}
          {consolidatedData && (
            <div className="stat-card bg-gradient-to-r from-primary/5 to-accent/5">
              <h3 className="font-heading font-bold text-lg mb-4 text-primary">GRAND SUMMARY</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-white rounded-sm shadow-sm">
                  <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Total Days</p>
                  <p className="font-mono font-bold text-2xl text-primary">{consolidatedData.total_days}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-sm shadow-sm">
                  <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Total Entries</p>
                  <p className="font-mono font-bold text-2xl text-accent">{consolidatedData.grand_summary?.total_entries}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-sm shadow-sm">
                  <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Total Planned</p>
                  <p className="font-mono font-bold text-2xl text-slate-700">{consolidatedData.grand_summary?.total_planned?.toFixed(2)}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-sm shadow-sm">
                  <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Total Actual</p>
                  <p className="font-mono font-bold text-2xl text-success">{consolidatedData.grand_summary?.total_actual?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Consolidated Entries by Date */}
          {consolidatedData?.consolidated_data?.map((dayData) => (
            <div key={dayData.date} className="stat-card" data-testid={`consolidated-date-${dayData.date}`}>
              {/* Date Header - Clickable to expand/collapse */}
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleDateExpansion(dayData.date)}
              >
                <div className="flex items-center space-x-4">
                  <div className="bg-primary text-white px-4 py-2 rounded-sm">
                    <Calendar className="w-5 h-5 inline mr-2" />
                    <span className="font-bold">{dayData.date}</span>
                  </div>
                  <div className="flex items-center space-x-6 text-sm">
                    <span className="text-slate-600">
                      <strong className="text-primary">{dayData.summary.activities_count}</strong> activities
                    </span>
                    <span className="text-slate-600">
                      Achievement: <strong className={dayData.summary.achievement_percentage >= 100 ? "text-success" : "text-warning"}>{dayData.summary.achievement_percentage}%</strong>
                    </span>
                    <span className="text-slate-600">
                      Workforce: <strong>{dayData.summary.total_workforce}</strong>
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {expandedDates[dayData.date] ? (
                    <ChevronUp className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  )}
                </div>
              </div>

              {/* Day Summary */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div className="bg-slate-50 p-3 rounded-sm">
                  <p className="text-xs text-slate-500 uppercase">Planned</p>
                  <p className="font-mono font-semibold">{dayData.summary.total_planned_quantity?.toFixed(2)}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-sm">
                  <p className="text-xs text-slate-500 uppercase">Actual</p>
                  <p className="font-mono font-semibold text-success">{dayData.summary.total_actual_quantity?.toFixed(2)}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-sm">
                  <p className="text-xs text-slate-500 uppercase">Weather</p>
                  <p className="font-medium">{dayData.summary.weather || "N/A"}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-sm">
                  <p className="text-xs text-slate-500 uppercase flex items-center"><Building2 className="w-3 h-3 mr-1" /> Contractors</p>
                  <p className="font-medium text-xs">{dayData.summary.contractors?.join(", ") || "None"}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-sm">
                  <p className="text-xs text-slate-500 uppercase flex items-center"><HardHat className="w-3 h-3 mr-1" /> Supervisors</p>
                  <p className="font-medium text-xs">{dayData.summary.supervisors?.join(", ") || "None"}</p>
                </div>
              </div>

              {/* Expanded Individual Entries */}
              {expandedDates[dayData.date] && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <h4 className="font-heading font-semibold text-sm mb-3 text-slate-700">Individual Entries</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left px-3 py-2 font-semibold">Activity</th>
                          <th className="text-left px-3 py-2 font-semibold">Contractor</th>
                          <th className="text-left px-3 py-2 font-semibold">Supervisor</th>
                          <th className="text-right px-3 py-2 font-semibold">Planned</th>
                          <th className="text-right px-3 py-2 font-semibold">Actual</th>
                          <th className="text-center px-3 py-2 font-semibold">Unit</th>
                          <th className="text-center px-3 py-2 font-semibold">Materials</th>
                          <th className="text-center px-3 py-2 font-semibold">Status</th>
                          <th className="text-center px-3 py-2 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayData.entries.map((entry) => (
                          <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2">{entry.activity}</td>
                            <td className="px-3 py-2 text-slate-600">{entry.contractor_name || "-"}</td>
                            <td className="px-3 py-2 text-slate-600">{entry.deployed_person_name || "-"}</td>
                            <td className="px-3 py-2 text-right font-mono">{entry.planned_quantity}</td>
                            <td className="px-3 py-2 text-right font-mono text-success">{entry.actual_quantity}</td>
                            <td className="px-3 py-2 text-center">{entry.unit}</td>
                            <td className="px-3 py-2 text-center">
                              {(entry.materials_recalculated || entry.auto_deducted_materials?.length > 0) ? (
                                <span className="px-1.5 py-0.5 text-xs rounded-sm bg-green-100 text-green-700">✓</span>
                              ) : entry.boq_item_id ? (
                                <span className="px-1.5 py-0.5 text-xs rounded-sm bg-orange-100 text-orange-700">⏳</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">{getStatusBadge(entry.status)}</td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={(e) => { e.stopPropagation(); viewDPR(entry.id); }}
                                className="text-primary hover:text-primary/80 p-1"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}

          {(!consolidatedData || consolidatedData.consolidated_data?.length === 0) && (
            <div className="text-center py-16 stat-card">
              <Layers className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <h3 className="font-heading font-bold text-xl mb-2">No Consolidated Data</h3>
              <p className="text-slate-600">No DPR entries found for the selected date range.</p>
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
      <div className="space-y-4">
        {dprEntries.map((dpr) => (
          <div key={dpr.id} className={`stat-card ${dpr.status === "Rejected" ? "border-l-4 border-l-error" : ""}`} data-testid={`dpr-entry-${dpr.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-1">
                  <h3 className="font-heading font-bold text-xl text-primary">{dpr.activity}</h3>
                  {getStatusBadge(dpr.status, dpr.is_locked)}
                  {/* Material Deduction Status Badge */}
                  {(dpr.materials_recalculated || dpr.auto_deducted_materials?.length > 0) ? (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-green-100 text-green-700" title="Materials have been auto-deducted from stock based on BOQ recipe">
                      ✓ Materials Deducted
                    </span>
                  ) : dpr.boq_item_id ? (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-sm bg-orange-100 text-orange-700" title="Materials not yet deducted - click Recalculate to process">
                      ⏳ Pending Deduction
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center space-x-4 text-sm text-slate-600">
                  <span className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {dpr.date}
                  </span>
                  <span className="flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    {dpr.unit}
                  </span>
                  <span className="flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    {dpr.created_by_name}
                  </span>
                </div>
                {dpr.status === "Rejected" && dpr.rejection_reason && (
                  <p className="text-sm text-error mt-2 bg-error/10 px-2 py-1 rounded-sm">
                    Rejection reason: {dpr.rejection_reason}
                  </p>
                )}
              </div>
              <div className="flex items-start space-x-3">
                <div className="text-right">
                  <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Achievement</p>
                  <p className="font-mono font-bold text-2xl text-success">{dpr.actual_quantity}</p>
                  <p className="text-xs text-slate-600">Planned: <span className="font-mono">{dpr.planned_quantity}</span></p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <div className="grid md:grid-cols-3 gap-4 text-sm flex-1">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Workforce</p>
                  <p className="font-mono font-semibold">{dpr.workforce_count} Workers</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Weather</p>
                  <p className="font-medium">{dpr.weather}</p>
                </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-600 mb-1">Equipment</p>
                <p className="font-medium">{dpr.equipment_used}</p>
              </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => viewDPR(dpr.id)}
                  data-testid={`view-dpr-${dpr.id}`}
                  className="btn-outline flex items-center space-x-1 text-sm px-3 py-1 border-accent text-accent hover:bg-accent/10"
                >
                  <Eye className="w-4 h-4" />
                  <span>View</span>
                </button>
                <button
                  onClick={() => exportDPR(dpr.id, dpr.activity, dpr.date)}
                  data-testid={`export-dpr-${dpr.id}`}
                  className="btn-outline flex items-center space-x-1 text-sm px-3 py-1"
                >
                  <Download className="w-4 h-4" />
                  <span>PDF</span>
                </button>
                
                {/* Submit Button - only for Draft or Rejected */}
                {(dpr.status === "Draft" || dpr.status === "Rejected" || !dpr.status) && (
                  <button
                    onClick={() => submitDPR(dpr.id)}
                    data-testid={`submit-dpr-${dpr.id}`}
                    className="btn-outline flex items-center space-x-1 text-sm px-3 py-1 border-accent text-accent hover:bg-accent/10"
                  >
                    <Send className="w-4 h-4" />
                    <span>Submit</span>
                  </button>
                )}
                
                {/* Edit Button - for users with edit permission, not for approved DPRs unless Master Admin */}
                {canEditDPR() && (dpr.status !== "Approved" || currentUser.role === "Master Admin") && (
                  <button
                    onClick={() => openEditDialog(dpr)}
                    data-testid={`edit-dpr-${dpr.id}`}
                    className="btn-outline flex items-center space-x-1 text-sm px-3 py-1 border-primary text-primary hover:bg-primary/10"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                )}
                
                {/* Delete Button - for users with edit permission */}
                {canEditDPR() && (dpr.status !== "Approved" || currentUser.role === "Master Admin") && (
                  <button
                    onClick={() => deleteDPR(dpr.id)}
                    data-testid={`delete-dpr-${dpr.id}`}
                    className="btn-outline flex items-center space-x-1 text-sm px-3 py-1 border-error text-error hover:bg-error/10"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                )}
                
                {/* Approve/Reject - only for Submitted and users who can approve */}
                {dpr.status === "Submitted" && canApprove && (
                  <>
                    <button
                      onClick={() => approveDPR(dpr.id)}
                      data-testid={`approve-dpr-${dpr.id}`}
                      className="btn-primary flex items-center space-x-1 text-sm px-3 py-1 bg-success hover:bg-success/90"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Approve</span>
                    </button>
                    <button
                      onClick={() => openRejectDialog(dpr)}
                      data-testid={`reject-dpr-${dpr.id}`}
                      className="btn-outline flex items-center space-x-1 text-sm px-3 py-1 border-error text-error hover:bg-error/10"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Reject</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {dpr.remarks && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs uppercase tracking-widest text-slate-600 mb-2">Remarks</p>
                <p className="text-sm">{dpr.remarks}</p>
              </div>
            )}

            {dpr.delays && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs uppercase tracking-widest text-red-600 mb-2">Delays</p>
                <p className="text-sm text-red-700 font-medium">{dpr.delays}</p>
              </div>
            )}

            {dpr.photos && dpr.photos.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <p className="text-xs uppercase tracking-widest text-slate-600 mb-3">Site Photos</p>
                <div className="grid grid-cols-3 gap-3">
                  {dpr.photos.map((photo, index) => (
                    <img
                      key={index}
                      src={`${BACKEND_URL}${photo}`}
                      alt={`Site photo ${index + 1}`}
                      className="w-full h-32 object-cover rounded-sm border border-slate-300"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {dprEntries.length === 0 && (
          <div className="text-center py-16 stat-card">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            <h3 className="font-heading font-bold text-xl mb-2">No DPR Entries</h3>
            <p className="text-slate-600">No daily progress reports have been submitted yet.</p>
          </div>
        )}
      </div>
      )}

      {/* View DPR Detail Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">DPR Details</DialogTitle>
          </DialogHeader>
          {viewDprData && (
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Activity</p>
                  <p className="font-semibold text-lg">{viewDprData.activity}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Date</p>
                  <p className="font-semibold text-lg">{viewDprData.date}</p>
                </div>
              </div>

              {/* Quantities */}
              <div className="grid grid-cols-3 gap-4 bg-slate-50 p-4 rounded-sm">
                <div className="text-center">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Planned</p>
                  <p className="font-mono font-bold text-2xl">{viewDprData.planned_quantity}</p>
                  <p className="text-sm text-slate-600">{viewDprData.unit}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Actual</p>
                  <p className="font-mono font-bold text-2xl text-success">{viewDprData.actual_quantity}</p>
                  <p className="text-sm text-slate-600">{viewDprData.unit}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Achievement</p>
                  <p className={`font-mono font-bold text-2xl ${viewDprData.planned_quantity > 0 && (viewDprData.actual_quantity / viewDprData.planned_quantity) >= 1 ? "text-success" : "text-warning"}`}>
                    {viewDprData.planned_quantity > 0 ? ((viewDprData.actual_quantity / viewDprData.planned_quantity) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>

              {/* Contractor & Supervisor */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-blue-600 mb-2 flex items-center">
                    <Building2 className="w-4 h-4 mr-1" /> Contractor
                  </p>
                  {viewDprData.contractor_details ? (
                    <div>
                      <p className="font-semibold">{viewDprData.contractor_details.name}</p>
                      <p className="text-sm text-slate-600">{viewDprData.contractor_details.contact_person}</p>
                      <p className="text-sm text-slate-600">{viewDprData.contractor_details.phone}</p>
                    </div>
                  ) : (
                    <p className="text-slate-500">{viewDprData.contractor_name || "Not assigned"}</p>
                  )}
                </div>
                <div className="bg-green-50 p-4 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-green-600 mb-2 flex items-center">
                    <HardHat className="w-4 h-4 mr-1" /> Deployed Persons / Supervisors
                  </p>
                  {viewDprData.deployed_persons && viewDprData.deployed_persons.length > 0 ? (
                    <div className="space-y-2">
                      {viewDprData.deployed_persons.map((person, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-xs font-semibold text-green-700">
                            {idx + 1}
                          </span>
                          <div>
                            <p className="font-semibold text-sm">{person.name}</p>
                            {person.designation && <p className="text-xs text-slate-600">{person.designation}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : viewDprData.deployed_person_details ? (
                    <div>
                      <p className="font-semibold">{viewDprData.deployed_person_details.name}</p>
                      <p className="text-sm text-slate-600">{viewDprData.deployed_person_details.designation}</p>
                      <p className="text-sm text-slate-600">{viewDprData.deployed_person_details.employee_code}</p>
                    </div>
                  ) : (
                    <p className="text-slate-500">{viewDprData.deployed_person_name || "Not assigned"}</p>
                  )}
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Workforce</p>
                  <p className="font-semibold">{viewDprData.workforce_count} Workers</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Weather</p>
                  <p className="font-semibold">{viewDprData.weather || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Status</p>
                  {getStatusBadge(viewDprData.status, viewDprData.is_locked)}
                </div>
              </div>

              {/* Equipment */}
              {viewDprData.equipment_used && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Equipment Used</p>
                  <p className="text-sm bg-slate-50 p-3 rounded-sm">{viewDprData.equipment_used}</p>
                </div>
              )}

              {/* Materials Consumed */}
              {viewDprData.materials_consumed_details && viewDprData.materials_consumed_details.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Materials Consumed</p>
                  <div className="bg-slate-50 rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left px-3 py-2">Material</th>
                          <th className="text-right px-3 py-2">Quantity</th>
                          <th className="text-center px-3 py-2">Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewDprData.materials_consumed_details.map((mat, idx) => (
                          <tr key={idx} className="border-t border-slate-200">
                            <td className="px-3 py-2">{mat.name}</td>
                            <td className="px-3 py-2 text-right font-mono">{mat.quantity}</td>
                            <td className="px-3 py-2 text-center">{mat.unit}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Remarks */}
              {viewDprData.remarks && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Remarks</p>
                  <p className="text-sm bg-slate-50 p-3 rounded-sm">{viewDprData.remarks}</p>
                </div>
              )}

              {/* Delays */}
              {viewDprData.delays && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-red-600 mb-2">Delays Reported</p>
                  <p className="text-sm bg-red-50 p-3 rounded-sm text-red-700">{viewDprData.delays}</p>
                </div>
              )}

              {/* BOQ Item Link */}
              {viewDprData.boq_item_details && (
                <div className="border-t pt-4">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Linked BOQ Item</p>
                  <div className="bg-amber-50 p-3 rounded-sm">
                    <p className="font-semibold">{viewDprData.boq_item_details.item_code}</p>
                    <p className="text-sm text-slate-600">{viewDprData.boq_item_details.description}</p>
                  </div>
                </div>
              )}

              {/* Created By */}
              <div className="border-t pt-4 text-sm text-slate-500">
                <p>Created by: <strong>{viewDprData.created_by_name}</strong></p>
                <p>Created at: {viewDprData.created_at ? new Date(viewDprData.created_at).toLocaleString() : "N/A"}</p>
              </div>
              
              {/* Auto Deducted Materials Info */}
              {viewDprData.auto_deducted_materials && viewDprData.auto_deducted_materials.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-xs uppercase tracking-widest text-green-600 mb-2">Auto-Deducted Materials (from BOQ Recipe)</p>
                  <div className="bg-green-50 rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-green-100">
                          <th className="text-left px-3 py-2">Material</th>
                          <th className="text-right px-3 py-2">Quantity Deducted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewDprData.auto_deducted_materials.map((mat, idx) => (
                          <tr key={idx} className="border-t border-green-200">
                            <td className="px-3 py-2">{mat.material_name}</td>
                            <td className="px-3 py-2 text-right font-mono text-green-700">{mat.quantity?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Recalculate Materials Button (for DPRs without auto-deduction) */}
              {currentUser.role === "Master Admin" && !viewDprData.materials_recalculated && viewDprData.boq_item_details && (
                <div className="border-t pt-4">
                  <button
                    onClick={() => {
                      recalculateDPRMaterials(viewDprData.id, viewDprData.activity);
                      setViewDialogOpen(false);
                    }}
                    className="w-full py-2 bg-warning/20 text-warning hover:bg-warning/30 rounded-sm text-sm font-medium transition-colors"
                  >
                    Recalculate & Deduct Materials (Based on BOQ Recipe)
                  </button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    This will deduct materials from stock based on the BOQ recipe for this activity.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-error">Reject DPR</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Rejecting DPR for: <strong>{selectedDpr?.activity}</strong>
            </p>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">
                Rejection Reason
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-error"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setRejectDialogOpen(false)}
                className="btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={rejectDPR}
                className="btn-primary bg-error hover:bg-error/90"
              >
                Reject DPR
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit DPR Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Activity</label>
              <input
                type="text"
                value={editForm.activity || ""}
                onChange={(e) => setEditForm({...editForm, activity: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Quantity</label>
                <input
                  type="number"
                  value={editForm.quantity || 0}
                  onChange={(e) => setEditForm({...editForm, quantity: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Unit</label>
                <input
                  type="text"
                  value={editForm.unit || ""}
                  onChange={(e) => setEditForm({...editForm, unit: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Workers Count</label>
              <input
                type="number"
                value={editForm.workers_count || 0}
                onChange={(e) => setEditForm({...editForm, workers_count: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
              <textarea
                value={editForm.remarks || ""}
                onChange={(e) => setEditForm({...editForm, remarks: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button onClick={() => setEditDialogOpen(false)} className="btn-outline">Cancel</button>
              <button onClick={editDPR} className="btn-primary">Save Changes</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DPRList;